import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, MessageCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_NUMBER = "+919068155122";

const FALLBACK_IMG = "https://images.pexels.com/photos/2381469/pexels-photo-2381469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const categories = [
  { id: "all", label: "All" },
  { id: "royal", label: "Royal" },
  { id: "traditional", label: "Traditional" },
  { id: "contemporary", label: "Contemporary" },
  { id: "luxury", label: "Luxury" },
];

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "17 Hand Embroidery Services India | KarigariHub",
    description: "Explore 17 Indian hand embroidery types for lehengas and bridal wear: Zardozi, Aari, Dabka, Mirror Work, Gota Patti, Sequin, Crystal. Custom orders.",
    keywords: "hand embroidery services, lehenga embroidery services, Zardozi embroidery, Aari work embroidery, mirror work lehenga, gota patti lehenga, sequin lehenga, embroidered lehenga, chikankari lehenga, zardozi lehenga, thread work lehenga, pearl lehenga, bridal embroidery services, custom embroidery India, Dabka embroidery, Mirror work embroidery India, Tilla work, Mukaish embroidery, Pearl embroidery, Crystal work, Cutdana, Beadwork, Sequin embroidery, Gota work, lehenga embroidery designs",
    aiDescription: "KarigariHub services page listing all 17 hand embroidery techniques for lehengas, bridal wear, and custom garments: Zardozi (royal metallic thread), Aari (chain stitch), Dabka (coiled wire), Mirror Work (shisha), Tilla (Kashmir gold/silver), Mukaish (Lucknowi badla), Pearl, Crystal, Cutdana (faceted beads), Beadwork, Sequin, Gota Patti (Rajasthani ribbon), Jaal (all-over net), Jadau (stone-setting), Leather, Applique, and Resham (silk thread). Popular for lehenga embroidery: mirror work lehenga, zardozi lehenga, gota patti lehenga, sequin lehenga, chikankari lehenga, embroidered lehenga choli. Contact +91 9068155122.",
  });
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredServices(services);
    } else {
      setFilteredServices(services.filter(s => s.category === activeCategory));
    }
  }, [activeCategory, services]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
      setFilteredServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetQuote = (service) => {
    const message = `Hello! I want to get a quote for ${service.name} embroidery work.\n\nDetails I need:\n- Price estimate\n- Timeline\n- Minimum order`;
    window.open(`https://wa.me/${PHONE_NUMBER.replace('+', '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-16 bg-white" data-testid="services-header">
        <div className="container-main">
          <div className="max-w-3xl">
            <div className="accent-line mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4">
              Hand Embroidery Services
            </h1>
            <p className="text-muted-foreground">
              Explore India's rich embroidery heritage. 17 traditional techniques, each mastered by skilled artisans
              carrying centuries of artistry and tradition.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-16 z-30 bg-[#F9F9F7]/95 backdrop-blur-sm border-b border-border py-3">
        <div className="container-main">
          <div className="flex items-center gap-2 overflow-x-auto">
            <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`text-xs px-4 py-2 rounded-full border whitespace-nowrap transition-all ${activeCategory === cat.id ? 'bg-[#1A1A1A] text-white border-[#1A1A1A] font-medium' : 'bg-white border-border text-muted-foreground hover:border-[#D4AF37]/40 hover:text-foreground'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing" data-testid="services-grid">
        <div className="container-main">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-[4/5] bg-secondary animate-pulse" />
                  <div className="h-4 bg-secondary animate-pulse w-1/2" />
                  <div className="h-6 bg-secondary animate-pulse w-3/4" />
                </div>
              ))}
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">No services found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service) => (
                <div 
                  key={service.id} 
                  className="service-card group"
                  data-testid={`service-item-${service.id}`}
                >
                  <div 
                    className="service-card-image cursor-pointer relative"
                    onClick={() => setSelectedService(service)}
                  >
                    <img 
                      src={service.images?.[0]} 
                      alt={`${service.name} hand embroidery service India - custom orders by master artisans`}
                      loading="lazy"
                      onError={(e) => { e.target.src = FALLBACK_IMG; }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-500 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 gap-3">
                      <Button 
                        className="bg-white text-[#1A1A1A] hover:bg-white/90"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedService(service);
                        }}
                        data-testid={`view-details-${service.id}`}
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      <Button 
                        className="bg-[#25D366] text-white hover:bg-[#25D366]/90"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGetQuote(service);
                        }}
                        data-testid={`get-quote-btn-${service.id}`}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Get Quote
                      </Button>
                    </div>
                  </div>
                  <div className="service-card-content">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs uppercase tracking-wider bg-[#D4AF37]/10 text-[#D4AF37]">
                        {service.category}
                      </Badge>
                    </div>
                    <Link to={`/services/${service.id}`} className="hover:text-[#D4AF37] transition-colors">
                      <h3 className="font-heading text-xl font-semibold mb-2">{service.name}</h3>
                    </Link>
                    <p className="text-xs text-muted-foreground mb-2">{service.origin}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{service.description}</p>
                    <Link to={`/services/${service.id}`} className="text-[#D4AF37] text-xs font-medium mt-2 inline-block hover:underline" data-testid={`service-link-${service.id}`}>
                      Learn more about {service.name} &rarr;
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Service Detail Modal */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="service-detail-modal">
          {selectedService && (
            <div>
              <div className="relative aspect-[16/9] overflow-hidden">
                <img 
                  src={selectedService.images?.[0]} 
                  alt={`${selectedService.name} traditional Indian hand embroidery - KarigariHub`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = FALLBACK_IMG; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <Badge className="bg-[#D4AF37] text-white mb-2">{selectedService.category}</Badge>
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold">
                    {selectedService.name}
                  </h2>
                  <p className="text-white/70 mt-2">{selectedService.origin}</p>
                </div>
              </div>
              
              <div className="p-8 space-y-8">
                <p className="text-muted-foreground leading-relaxed">
                  {selectedService.description}
                </p>

                {selectedService.history && (
                  <div className="bg-[#F5F0E8] p-6 border-l-4 border-[#D4AF37]">
                    <h3 className="font-heading text-lg font-semibold mb-2">History</h3>
                    <p className="text-muted-foreground text-sm italic font-accent leading-relaxed">
                      {selectedService.history}
                    </p>
                  </div>
                )}

                {selectedService.images?.length > 1 && (
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-4">Gallery</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedService.images.map((img, idx) => (
                        <div key={idx} className="aspect-square overflow-hidden">
                          <img 
                            src={img} 
                            alt={`${selectedService.name} embroidery work sample ${idx + 1} - handcrafted by Indian artisan`} 
                            className="w-full h-full object-cover hover:scale-105 transition-transform"
                            onError={(e) => { e.target.src = FALLBACK_IMG; }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-3">Techniques</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedService.techniques?.map((tech, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{tech}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-3">Best For</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedService.best_for?.map((item, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{item}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-[#1A1A1A] text-white p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-1">Interested?</p>
                      <p className="font-heading text-xl">Get a Custom Quote</p>
                    </div>
                    <Button 
                      className="bg-[#25D366] text-white hover:bg-[#25D366]/90"
                      onClick={() => handleGetQuote(selectedService)}
                      data-testid="modal-get-quote-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Get Quote on WhatsApp
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A] text-white">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
            Need a Custom Order?
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Any embroidery, any design — our artisans can bring your vision to life. Share your requirements with us.
          </p>
          <Button 
            className="bg-[#25D366] text-white hover:bg-[#25D366]/90 px-8 py-3"
            onClick={() => {
              const message = "Hello! I have a custom embroidery requirement. Can we discuss?";
              window.open(`https://wa.me/${PHONE_NUMBER.replace('+', '')}?text=${encodeURIComponent(message)}`, '_blank');
            }}
            data-testid="custom-enquiry-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Custom Enquiry
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
